import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Spinner } from "../../components/ui/spinner";
import { getSystemStatus } from "../../lib/api";

const STATUS_VARIANT = { healthy: "success", degraded: "warning", down: "destructive", unknown: "muted" };
const STATUS_LABEL = { healthy: "Healthy", degraded: "Degraded", down: "Down", unknown: "Unknown" };

const TYPE_LABEL = { lambda: "Lambda Functions", dynamodb: "DynamoDB Tables", http: "External Endpoints" };

function formatCheckedAt(iso) {
  if (!iso) return "never";
  return new Date(iso).toLocaleString();
}

function ResourceGroup({ type, resources }) {
  if (resources.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{TYPE_LABEL[type] || type}</CardTitle>
        <CardDescription>{resources.length} tracked</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-slate-100">
          {resources.map((r) => (
            <div key={r.name} className="flex items-center justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-slate-800 truncate">{r.label || r.name}</p>
                <p className="text-xs text-slate-400 truncate">{r.detail}</p>
              </div>
              <Badge variant={STATUS_VARIANT[r.status] || "muted"} className="shrink-0">
                {STATUS_LABEL[r.status] || r.status}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SystemHealthPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      getSystemStatus().then((data) => {
        if (cancelled) return;
        setStatus(data);
        setLoading(false);
      });
    };
    poll();
    const interval = setInterval(poll, 86400000); // 24h
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // The status body is untrusted: anything but a list of objects is shown as no data
  // rather than crashing the page (TP-FMP-01-TS-02-TC-0010).
  const text = (v) => (typeof v === "string" || typeof v === "number" ? String(v) : "");
  const resources = (Array.isArray(status?.resources) ? status.resources : [])
    .filter((r) => r && typeof r === "object")
    .map((r) => ({ ...r, type: text(r.type), name: text(r.name), label: text(r.label), detail: text(r.detail),
      status: Object.prototype.hasOwnProperty.call(STATUS_LABEL, r.status) ? r.status : "unknown" }));
  const byType = resources.reduce((acc, r) => {
    acc[r.type] = acc[r.type] || [];
    acc[r.type].push(r);
    return acc;
  }, {});
  const downCount = resources.filter((r) => r.status !== "healthy").length;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">System Health</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Live status of every Lambda function and DynamoDB table tracked by the health canary.
        </p>
      </div>

      {status?.stale && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Snapshot is stale — the health canary hasn't reported in recently. Statuses below may be out of date.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Last checked: {formatCheckedAt(status?.checked_at)}
        </span>
        <span>
          {resources.length - downCount}/{resources.length} healthy
        </span>
      </div>

      {resources.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-slate-400">
            No snapshot available yet.
          </CardContent>
        </Card>
      ) : (
        Object.keys(TYPE_LABEL).map((type) => (
          <ResourceGroup key={type} type={type} resources={byType[type] || []} />
        ))
      )}
    </div>
  );
}
